import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, DollarSign, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Footer } from "./Footer";
import { usePWAStandalone } from "@/hooks/usePWAStandalone";

interface Transfer {
  from: string;
  to: string;
  amount: number;
}

export const SimplifiedPayoutsScreen = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [gameName, setGameName] = useState("");
  const [gameStatus, setGameStatus] = useState("");
  const isStandalone = usePWAStandalone();

  useEffect(() => {
    if (gameId) {
      calculateTransfers();
    }
  }, [gameId]);

  const calculateTransfers = async () => {
    try {
      // Get game details
      const { data: game, error: gameError } = await supabase
        .from('games')
        .select('title, location, status, group_id')
        .eq('id', gameId)
        .single();

      if (gameError) throw gameError;
      setGameName(game.title || game.location || 'Poker Game');
      setGameStatus(game.status);

      // Get all payouts for this game
      const { data: payouts, error } = await supabase
        .from('payouts')
        .select(`
          *,
          players:player_id (name)
        `)
        .eq('game_id', gameId);

      if (error) throw error;

      if (!payouts || payouts.length === 0) {
        setTransfers([]);
        setLoading(false);
        return;
      }

      // Calculate simplified transfers
      const balances: { [playerId: string]: { name: string; balance: number } } = {};
      
      payouts.forEach((payout: any) => {
        balances[payout.player_id] = {
          name: payout.players.name,
          balance: payout.net_amount
        };
      });

      // Separate debtors and creditors
      const debtors = Object.entries(balances).filter(([_, data]) => data.balance < 0);
      const creditors = Object.entries(balances).filter(([_, data]) => data.balance > 0);

      const calculatedTransfers: Transfer[] = [];

      // Calculate minimal transfers
      const debtorsCopy = debtors.map(([id, data]) => ({ id, name: data.name, amount: Math.abs(data.balance) }));
      const creditorsCopy = creditors.map(([id, data]) => ({ id, name: data.name, amount: data.balance }));

      while (debtorsCopy.length > 0 && creditorsCopy.length > 0) {
        const debtor = debtorsCopy[0];
        const creditor = creditorsCopy[0];

        const transferAmount = Math.min(debtor.amount, creditor.amount);

        calculatedTransfers.push({
          from: debtor.name,
          to: creditor.name,
          amount: transferAmount
        });

        debtor.amount -= transferAmount;
        creditor.amount -= transferAmount;

        if (debtor.amount === 0) {
          debtorsCopy.shift();
        }
        if (creditor.amount === 0) {
          creditorsCopy.shift();
        }
      }

      setTransfers(calculatedTransfers);
    } catch (error) {
      console.error('Error calculating transfers:', error);
      toast({
        title: "Error",
        description: "Failed to calculate simplified payouts",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Calculating simplified payouts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/game/${gameId}?tab=summary`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Game
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">Simplified Payouts</h1>
              <p className="text-sm text-muted-foreground">{gameName}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {transfers.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <DollarSign className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">No Transfers Required</h2>
              <p className="text-muted-foreground">
                All players are even - no money needs to change hands!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-center text-2xl">
                  Settlement Complete! 🎉
                </CardTitle>
                <p className="text-center text-muted-foreground">
                  Here's the simplest way to settle all debts
                </p>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DollarSign className="h-5 w-5 mr-2" />
                  Required Transfers ({transfers.length} transactions)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {transfers.map((transfer, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-6 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="text-center">
                        <div className="font-bold text-lg">{transfer.from}</div>
                        <div className="text-sm text-muted-foreground">pays</div>
                      </div>
                      <ArrowRight className="h-6 w-6 text-primary" />
                      <div className="text-center">
                        <div className="font-bold text-lg">{transfer.to}</div>
                        <div className="text-sm text-muted-foreground">receives</div>
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-primary">
                      ₹{transfer.amount.toFixed(2)}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-6 text-center">
                <h3 className="font-semibold text-lg mb-2">💡 Pro Tip</h3>
                <p className="text-muted-foreground">
                  This is the minimum number of transfers needed to settle all debts.
                  Complete these {transfers.length} transaction{transfers.length !== 1 ? 's' : ''} and everyone will be square!
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      {!isStandalone && <Footer />}
    </div>
  );
};